import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { paymentsApi } from '../../api';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Button } from '../../components/Button';
import { formatCurrency } from '../../utils/currency';

export const PaymentCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    try {
      // Get reference from URL parameters
      const reference = searchParams.get('reference') || searchParams.get('trxref');

      if (!reference) {
        setError('Payment reference not found in URL');
        setLoading(false);
        return;
      }

      // Verify the payment with backend
      const response = await paymentsApi.verify(reference);

      if (response.success && response.data) {
        setVerificationResult(response.data);
      } else {
        setError(response.message || 'Payment verification failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify payment');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="payment-callback-page">
        <div className="verification-container">
          <LoadingSpinner />
          <h2>Verifying your payment...</h2>
          <p>Please wait while we confirm your transaction.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-callback-page">
        <div className="verification-container error">
          <div className="status-icon error-icon">✕</div>
          <h2>Payment Verification Failed</h2>
          <p className="error-message">{error}</p>
          <div className="action-buttons">
            <Button onClick={() => navigate('/customer/dashboard')} variant="primary">
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const isSuccess = verificationResult?.status === 'success';

  return (
    <div className="payment-callback-page">
      <div className={`verification-container ${isSuccess ? 'success' : 'failed'}`}>
        <div className={`status-icon ${isSuccess ? 'success-icon' : 'error-icon'}`}>
          {isSuccess ? '✓' : '✕'}
        </div>

        <h2>
          {isSuccess ? 'Payment Successful!' : 'Payment Failed'}
        </h2>

        <p className="status-message">
          {isSuccess
            ? 'Your payment has been processed successfully and your loan balance has been updated.'
            : 'We were unable to process your payment. Please try again or contact support.'}
        </p>

        {verificationResult && (
          <div className="payment-details">
            <div className="detail-item">
              <span className="label">Reference:</span>
              <span className="value">{verificationResult.reference}</span>
            </div>
            <div className="detail-item">
              <span className="label">Amount:</span>
              <span className="value">
                {formatCurrency(verificationResult.amount)}
              </span>
            </div>
            {verificationResult.gateway_response && (
              <div className="detail-item">
                <span className="label">Payment Method:</span>
                <span className="value">
                  {verificationResult.gateway_response.channel || 'Card'}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="action-buttons">
          <Button onClick={() => navigate('/customer/loans')} variant="primary">
            View My Loans
          </Button>
          <Button onClick={() => navigate('/customer/dashboard')} variant="secondary">
            Go to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};
